import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import FileSearchBar from "../../components/filePacket/FileSearchBar";
import { AppLayout } from "../../components/layout/Header";
import AuditLogView from "../../components/messaging/AuditLogView";
import ConfirmClientModal from "../../components/client/ConfirmClientModal";
import SendPortalLinkModal from "../../components/client/SendPortalLinkModal";
import { emptyFileFilters, filterFileIndex, uniqueAgents } from "../../js/api/filePacket";
import { useDesk } from "../../js/hooks/useDesk";
import { useFileIndex } from "../../js/hooks/useFilePacket";
import { formatDate, prettyValue } from "../../js/utils/formatters";
import { messagesPath } from "../../js/utils/messagingPaths";
import { clientCodeForUser } from "../../js/api/clientAccess";
import { clientStatusLabel, lastClientActivity, setClientStatus } from "../../js/api/clientActivity";
import { inviteHref, listDeskInvites } from "../../js/api/clientInvite";
import SendInviteModal from "../../components/client/SendInviteModal";
import { getById } from "../../js/db";
import { useAuth } from "../../js/hooks/useAuth";
import { sellerFileId } from "../../js/api/filePacket/ids";

function daysOnMarket(iso) {
  if (!iso) return "—";
  const start = Date.parse(iso);
  if (!Number.isFinite(start)) return "—";
  const days = Math.max(0, Math.round((Date.now() - start) / (1000 * 60 * 60 * 24)));
  return `${days} day${days === 1 ? "" : "s"}`;
}

function stamp(iso) {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return `${formatDate(iso)} ${date.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}`;
}

export default function AgentClients() {
  const desk = useDesk();
  const { session } = useAuth();
  const { rows } = useFileIndex();
  const [filters, setFilters] = useState(emptyFileFilters);
  const [sort, setSort] = useState({ key: "when", dir: "desc" });
  const [confirmClient, setConfirmClient] = useState(null);
  const [sendClient, setSendClient] = useState(null);
  const [resendInvite, setResendInvite] = useState(null);
  const [tick, setTick] = useState(0);
  const pendingInvites = useMemo(
    () => listDeskInvites(session).filter((row) => row.status === "pending"),
    [session, tick]
  );
  const agents = useMemo(() => uniqueAgents(rows), [rows]);
  const filtered = useMemo(() => filterFileIndex(rows, filters), [rows, filters, tick]);
  const deskRole = desk.variant === "broker" ? "broker" : "agent";

  const sorted = useMemo(() => {
    const copy = [...filtered];
    copy.sort((a, b) => {
      const dir = sort.dir === "asc" ? 1 : -1;
      if (sort.key === "client") return a.name.localeCompare(b.name) * dir;
      if (sort.key === "status") return String(a.status).localeCompare(String(b.status)) * dir;
      if (sort.key === "type") return String(a.fileType).localeCompare(String(b.fileType)) * dir;
      return String(a.when).localeCompare(String(b.when)) * dir;
    });
    return copy;
  }, [filtered, sort]);

  function toggleSort(key) {
    setSort((current) =>
      current.key === key ? { key, dir: current.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }
    );
  }

  function sortMark(key) {
    if (sort.key !== key) return "";
    return sort.dir === "asc" ? " ↑" : " ↓";
  }

  return (
    <AppLayout variant={desk.variant === "broker" ? "broker" : "agent"}>
      <div className="page-header">
        <div>
          <h1>Client Manager</h1>
          <p className="page-lead">
            Invite a client first. Their Client ID is created when they finish setup, then this list
            tracks their work with this advisor.
          </p>
          <p>
            <Link className="primary-btn" to="/client/register">
              Invite a client
            </Link>
          </p>
        </div>
      </div>

      {pendingInvites.length ? (
        <div className="card stack" style={{ marginBottom: "1rem" }}>
          <h2>Invitations waiting</h2>
          <ul className="account-sessions">
            {pendingInvites.map((row) => (
              <li key={row.token}>
                {row.name} · {row.email} · {row.kind === "seller" ? "Seller" : "Buyer"}
                <div className="muted">{inviteHref(row.token)}</div>
                <button type="button" className="link-btn" onClick={() => setResendInvite(row)}>
                  Resend invitation
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className="card stack" style={{ marginBottom: "1rem" }}>
        <FileSearchBar filters={filters} agents={agents} onChange={setFilters} idPrefix="client-search" />
        <p className="muted">
          {sorted.length} client{sorted.length === 1 ? "" : "s"}
          {sorted.length !== rows.length ? ` of ${rows.length}` : ""}
        </p>
      </div>

      <div className="card" style={{ overflowX: "auto" }} data-tour="desk-clients">
        <table className="sort-table">
          <thead>
            <tr>
              <th>
                <button type="button" onClick={() => toggleSort("when")}>
                  Contact Date/Time{sortMark("when")}
                </button>
              </th>
              <th>
                <button type="button" onClick={() => toggleSort("client")}>
                  Name{sortMark("client")}
                </button>
              </th>
              <th>Client ID</th>
              <th>
                <button type="button" onClick={() => toggleSort("type")}>
                  Buyer/Seller{sortMark("type")}
                </button>
              </th>
              <th>
                <button type="button" onClick={() => toggleSort("status")}>
                  Status{sortMark("status")}
                </button>
              </th>
              <th>Last activity</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((row) => {
              const client = row.clientId ? getById("users", row.clientId) : null;
              const code = row.clientId ? clientCodeForUser(row.clientId) : "";
              const last = row.clientId ? lastClientActivity(row.clientId) : null;
              const profileTo =
                row.fileType === "seller"
                  ? desk.file(row.id || sellerFileId(row.clientId))
                  : row.clientId
                    ? desk.client(row.clientId)
                    : desk.file(row.id);
              return (
                <tr key={row.id}>
                  <td>{stamp(row.when)}</td>
                  <td>
                    <strong>{row.name || "Client"}</strong>
                    <div className="muted">{daysOnMarket(row.when)} on file</div>
                  </td>
                  <td>{code || "—"}</td>
                  <td>{prettyValue(row.fileType)}</td>
                  <td>
                    {client ? clientStatusLabel(client.status) : row.statusLabel}
                    <div className="muted">Checklist {row.progress}%</div>
                  </td>
                  <td>{last ? stamp(last.at) : stamp(row.when)}</td>
                  <td>
                    <p className="row" style={{ gap: "0.65rem", margin: 0, flexWrap: "wrap" }}>
                      <button type="button" className="primary-btn" onClick={() => client && setConfirmClient(client)}>
                        View client
                      </button>
                      <button type="button" className="ghost-btn" onClick={() => client && setSendClient(client)}>
                        Send Link
                      </button>
                      <Link to={profileTo} className="ghost-btn">
                        Edit
                      </Link>
                      {client ? (
                        <button
                          type="button"
                          className="ghost-btn"
                          onClick={() => {
                            setClientStatus(client.id, client.status === "suspended" ? "active" : "suspended", session);
                            setTick((value) => value + 1);
                          }}
                        >
                          {client.status === "suspended" ? "Restore" : "Suspend"}
                        </button>
                      ) : null}
                      {client ? (
                        <button
                          type="button"
                          className="ghost-btn"
                          onClick={() => {
                            setClientStatus(client.id, "archived", session);
                            setTick((value) => value + 1);
                          }}
                        >
                          Archive
                        </button>
                      ) : null}
                      <Link
                        to={
                          row.transactionId
                            ? messagesPath(deskRole, { transactionId: row.transactionId })
                            : desk.messages
                        }
                        className="ghost-btn"
                      >
                        Messaging
                      </Link>
                    </p>
                  </td>
                </tr>
              );
            })}
            {!sorted.length ? (
              <tr>
                <td colSpan={7} className="muted">
                  {desk.variant === "broker"
                    ? "No clients match this desk or search. Switch My Business / All Agents if you expected files here."
                    : "No clients match this search."}
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      <div style={{ marginTop: "1.5rem" }}>
        <AuditLogView title="Assigned-file log" />
      </div>
      <ConfirmClientModal
        client={confirmClient}
        isOpen={Boolean(confirmClient)}
        onClose={() => setConfirmClient(null)}
        profileTo={
          confirmClient
            ? confirmClient.role_type === "seller"
              ? desk.file(sellerFileId(confirmClient.id))
              : desk.client(confirmClient.id)
            : ""
        }
      />
      <SendPortalLinkModal
        client={sendClient}
        session={session}
        isOpen={Boolean(sendClient)}
        onClose={() => setSendClient(null)}
      />
      <SendInviteModal
        invite={resendInvite}
        isOpen={Boolean(resendInvite)}
        onClose={() => {
          setResendInvite(null);
          setTick((value) => value + 1);
        }}
      />
    </AppLayout>
  );
}
